"use client";

import CustomScrollbar from "@/components/custom-scrollbar";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { renderToString } from "react-dom/server";

/**
 * Custom dropdown form input
 * @param {Object} props
 * @param {String} props.name Form input name
 * @param {String | import("react").ReactNode} props.label Form input label
 * @param {Boolean} [props.hiddenLabel] Hide form input label
 * @param {Boolean | Number} [props.enableCustomScrollbar] Enables custom scrollbar
 * @param {String} [props.namespace] Use is multiple dropdowns use same name
 * @param {Array} props.itemList // [{ key, id, label, labelText, disabled, indented, code, bold }, ...] Use useMemo.
 * @param {String | undefined} [props.placeholder] Form input placeholder
 * @param {String | undefined} [props.placeholderErrorMessage] Form input placeholder
 * @param {String} [props.buttonAriaLabel] Aria-label
 * @param {Function | undefined} [props.onChange] Runs method on change
 * @param {React.FocusEventHandler<HTMLFieldSetElement> | undefined} [props.onBlur] Runs method on blur (focus out)
 * @param {Boolean} [props.required] Required flag
 * @param {Boolean} [props.disabled] Disabled flag
 * @param {Boolean} [props.disableFirstItem] Removes first list item click events
 * @param {Boolean} [props.boldFirstItem] Bolds firts list item
 * @param {Boolean} [props.omitFirstItem] Completely removes the first list item
 * @param {Boolean} [props.omitBlurValidate] Omits auto validation on blur, useful when input is not required
 * @param {Boolean} [props.boldOptionSelected] Bolds slected option
 * @param {Function | undefined} [props.onValidate] Runs methe on validation
 * @param {import("react").ReactNode} [props.errorMessage] Validation error message
 * @param {String} [props.customClass] Add custom class names
 * @param {*} ref 
 * @returns ReactNode
 */
function FormInputDropdown(
  {
    name,
    label,
    namespace = '',
    placeholder = undefined,
    placeholderErrorMessage = undefined,
    itemList,
    buttonAriaLabel = "",
    onChange = undefined,
    onBlur = undefined,
    required = false,
    disabled = undefined,
    disableFirstItem = true,
    boldFirstItem = false,
    omitFirstItem = false,
    omitBlurValidate = false,
    boldOptionSelected = false,
    onValidate = undefined,
    errorMessage = <>This field is required</>,
    customClass = undefined,
    hiddenLabel = false,
    enableCustomScrollbar = false,
    ...rest
  },
  ref
) {
  const [valid, setValid] = useState(true);
  const [optionSelected, setOptionSelected] = useState("");
  const [active, setActive] = useState(false);
  const [currentCheckedElement, setCurrentCheckedElement] = useState(null);
  const [previousOptionSelected, setPreviousOptionSelected] = useState("");
  const [currentValue, setCurrentValue] = useState("");
  const [code, setCode] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const selectListRef = useRef(null);
  const [temporaryDisableSelectList, setTemporaryDisableSelectList] = useState(false);

  useEffect(() => {
    if (active) {
      if (!valid && placeholderErrorMessage) {
        setOptionSelected(placeholderErrorMessage);
      } else {
        setOptionSelected(placeholder);
      }
    } else if (previousOptionSelected) {
      setOptionSelected(previousOptionSelected);
    } else {
      if (!valid && placeholderErrorMessage) {
        setOptionSelected(placeholderErrorMessage);
      } else {
        !disabled && setOptionSelected(placeholder);
      }
    }

    const handleOutsideClick = (e) => {
      if (active && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        if (required && optionSelected === placeholder) {
          validate();
        }
        setActive(false);
      }
    }

    window.addEventListener("click", handleOutsideClick);

    return () => {
      window.removeEventListener("click", handleOutsideClick);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, placeholder, previousOptionSelected, valid, placeholderErrorMessage]);

  useEffect(() => {
    if (typeof onChange === "function" && currentValue !== "") {
      onChange({ currentValue, code, optionSelected });
    }
    // console.log(currentValue);
    required && setValid(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (active && selectListRef.current) {
      selectListRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleToggle = (e) => {
    if ('readOnly' in rest && rest.readOnly) {
      return;
    }

    e.stopPropagation();
    setActive((prevActive) => !prevActive);
  }

  const handleSelect = (e) => {
    const { value, disabled: itemDisabled, code } = e.currentTarget.dataset;
    if (itemDisabled === "true") {
      e.preventDefault();
      return
    }
    setOptionSelected(value);
    setCode(code);
    setPreviousOptionSelected(value);
    setCurrentValue(e.currentTarget.htmlFor);
    if (currentCheckedElement) {
      currentCheckedElement.checked = false;
    }
    e.currentTarget.checked = true;
    setCurrentCheckedElement(e.currentTarget);
    setActive(false);

    buttonRef.current?.focus();
  }

  const handleKeyPress = (e) => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault();

      const { value, disabled: itemDisabled, code } = e.currentTarget.dataset;
      if (itemDisabled === "true") {
        return;
      }
      setCode(code);
      setOptionSelected(value);
      setPreviousOptionSelected(value);
      setCurrentValue(e.currentTarget.htmlFor);
      if (currentCheckedElement) {
        currentCheckedElement.checked = false;
      }
      e.currentTarget.checked = true
      setCurrentCheckedElement(e.currentTarget);
      setActive(false);

      buttonRef.current?.focus();
    }
  }

  const labelValue = label;
  const groupName = `group-${name}`;
  const itemListData =
    !omitFirstItem
      ? [
          {
            key: 0,
            id: `${namespace}${name}-select-id-0`,
            label: placeholder,
            labelText: placeholder,
            itemLabel: undefined,
            disabled: disableFirstItem,
            indented: false,
            bold: boldFirstItem,
            code: "",
          },
          ...itemList?.map((item, index) => ({
            key: index + 1,
            id: `${namespace}${name}-select-id-${index + 1}`,
            label: item.label,
            labelText: item.labelText || renderToString(item.label).replace(/<[^>]+>/g, ''),
            itemLabel: item.itemLabel,
            disabled: item.disabled !== undefined && item.disabled,
            indented: item.indented !== undefined && item.indented,
            bold: item.bold !== undefined && item.bold,
            code: item.code,
            ...item.rest,
          })),
        ]
      : itemList?.map((item, index) => ({
          key: index,
          id: `${namespace}${name}-select-id-${index}`,
          label: item.label,
          labelText: item.labelText || renderToString(item.label).replace(/<[^>]+>/g, ''),
          itemLabel: item.itemLabel,
          disabled: (index === 0 && disableFirstItem) || (item.disabled !== undefined && item.disabled),
          indented: item.indented !== undefined && item.indented,
          bold: item.bold !== undefined && item.bold,
          code: item.code,
          ...item.rest,
        }));
  
  const containerClass = useMemo(() => {
    var className = "form-input-dropdown";

    if (active) {
      className = `${className} form-input-dropdown--active`;
    }

    if (!valid) {
      className = `${className} form-input-dropdown--invalid`;
    }

    if (customClass) {
      className = `${className} ${customClass}`;
    }

    if (!active && placeholder !== optionSelected) {
      className = `${className} form-input-dropdown--selected`;
    }

    if (enableCustomScrollbar !== false && typeof enableCustomScrollbar === "number" && enableCustomScrollbar > 0) {
      className = `${className} form-input-dropdown--custom-scrollbar`;
    }
    return className
  }, [active, valid, customClass, placeholder, optionSelected, enableCustomScrollbar]);

  function validate() {
    const valid = !required || (required && currentValue !== '');
    
    if (onValidate !== undefined) {
      onValidate({name, valid});
    }

    setValid(valid);
    return valid;
  }

  function testValidation() {
    return !required || (required && currentValue !== '');
  }

  function reset() {
    if (currentCheckedElement) {
      currentCheckedElement.checked = false;
      const input = dropdownRef.current?.querySelector(`input#${currentValue}`);
      if (input) {
        input.checked = false;
      }
    }
    setValid(true);
    setActive(false);
    setCurrentValue("");
    setOptionSelected(placeholder);
    setCode("");
    setPreviousOptionSelected("");
    setCurrentCheckedElement(null);
  }

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemList])

  useImperativeHandle(ref, () => ({
    name,
    validate() {
      return validate();
    },
    testValidation() {
      return testValidation();
    },
    get currentValue() {
      return currentValue;
    },
    getCode() {
      return code;
    },
    reset() {
      reset();
    },
    setValid(value) {
      setValid(value);
    },
    setIndexValue(index) {
      const indexData = itemListData[index];
      if (!indexData) return;

      const currentElement = dropdownRef.current?.querySelector(`label[htmlFor="${indexData.id}"]`);

      setCode(indexData.code);
      setOptionSelected(indexData.labelText);
      setPreviousOptionSelected(indexData.labelText);
      setCurrentValue(indexData.id);
      if (currentCheckedElement) {
        currentCheckedElement.checked = false;
      }

      if (currentElement){
        currentElement.checked = true;
        setCurrentCheckedElement(currentElement);
      }
      setActive(false);
    },
    get optionSelected() {
      return optionSelected;
    },
    get code() {
      return code;
    },
    get hasSelection() {
      return optionSelected !== placeholder && optionSelected !== placeholderErrorMessage;
    },
    get type() {
      return 'dropdown';
    },
    get indexValue() {
      return itemListData.findIndex(item => item.id === currentValue);
    },
    setFocus() {
      const button = buttonRef.current;
      button?.focus({ focusVisible: true });
      if (window.innerWidth < 699) {
        button?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
  }));

  const optionSelectedLabel = useMemo(() => {
    const selectedOption = itemListData.find(item => item.id === currentValue);
    return selectedOption ? selectedOption.label : optionSelected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue, itemListData, placeholder, optionSelected, valid]);

  return (
    <fieldset
      ref={dropdownRef}
      className={containerClass}
      onBlur={(e) => {
        e.type === 'blur' && !omitBlurValidate && !disabled && !active && validate();
        !disabled && !active && onBlur !== undefined && onBlur(e);
      }}
      id={`form_input_${name}`}
      disabled={disabled}
    >
      {labelValue && <legend id={`form_input_${name}_legend`} className={hiddenLabel ? 'sr-only' : undefined}>{(!valid && errorMessage && required) || required ? '*' : ''}{labelValue}</legend>}
      <button
        type="button"
        ref={buttonRef}
        className="form-input-dropdown__select-button"
        aria-label={buttonAriaLabel}
        aria-haspopup="true"
        aria-expanded={active}
        onClick={handleToggle}
        aria-labelledby={`form_input_${name}_legend`}
        draggable="false"
        tabIndex={0}
        data-gtm-event-category="Form"
        data-gtm-event-action="Click"
        data-gtm-event-label={renderToString(label).replace(/<[^>]+>/g, '')}
        role="listbox"
      >
        {boldOptionSelected ? <strong>{optionSelectedLabel}</strong> : <>{optionSelectedLabel}</>}
      </button>
      <ul
        className={`form-input-dropdown__select-list${active ? ' form-input-dropdown__select-list--active' : ''}`}
        role="menu"
        ref={selectListRef}
        style={enableCustomScrollbar !== false && typeof enableCustomScrollbar === "number" && enableCustomScrollbar > 0 ? {
          maxHeight: `${enableCustomScrollbar}px`,
          overflowY: 'auto',
        } : {}}
        aria-labelledby={`form_input_${name}_legend`}
      >
        {itemListData.map(
          ({ key, id, label, labelText, itemLabel, disabled: itemDisabled, indented, code, bold, ...restProps }) => (
            <li
              role="presentation"
              key={key}
              className={`form-input-dropdown__item ${
                indented ? "form-input-dropdown--indented" : ""
              }`}
            >
              <input
                type="radio"
                id={id}
                name={groupName}
                value={labelText}
                role="menuitem"
                tabIndex={-1}
                data-value={(labelText)}
                data-code={code}
                aria-errormessage={!valid ? `form_input_${name}_error_message` : undefined}
                data-gtm-event-label={labelText}
                {...rest}
              />
              <label
                htmlFor={id}
                tabIndex={0}
                data-value={labelText}
                data-code={code}
                data-disabled={(itemDisabled || temporaryDisableSelectList).toString()}
                onClick={handleSelect}
                onKeyDown={handleKeyPress}
                data-indented={indented.toString()}
                className={`${bold ? 'font-bold' : ''}${currentValue === id || (key === 0 && currentValue === '') ? ' selected' : ''}`}
                draggable="false"
                data-gtm-event-category="Form"
                data-gtm-event-action="Click"
                data-gtm-event-label={renderToString(label).replace(/<[^>]+>/g, '')}
                {...restProps}
              >
                {itemLabel ? itemLabel : label}
              </label>
            </li>
          )
        )}
      </ul>
      {enableCustomScrollbar && active && <CustomScrollbar contentContainerRef={selectListRef} enableCustomScrollbar={enableCustomScrollbar} onDrag={(dragging) => {
        setTemporaryDisableSelectList(dragging && active);
      }} />}
      {errorMessage && !valid && <div id={`form_input_${name}_error_message`} className="invalid-feedback text-sm mt-1">{errorMessage}</div>}
    </fieldset>
  )
}

export default forwardRef(FormInputDropdown);
